import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n";
import { CONTACT, waLink } from "@/data/site";
import { Phone, MessageCircle, MapPin, ShieldCheck, Star } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/8442034/pexels-photo-8442034.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";

export default function Hero({ onBook }) {
  const { t } = useLang();
  const mapsUrl = CONTACT.mapsLink;

  return (
    <section id="top" className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden" data-testid="hero-section">
      <div className="absolute top-0 right-0 w-[55%] h-[80%] bg-turq/5 rounded-bl-[200px] -z-10" />
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-turq/10 rounded-full blur-3xl -z-10" />

      <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
          <div className="inline-flex items-center gap-2 bg-turq/10 text-turq-dark rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wider mb-6" data-testid="hero-badge">
            <ShieldCheck className="w-4 h-4" /> Certified Pathology Lab • Phillaur
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.05] font-bold text-navy tracking-tight">
            {t("hero_title")}
          </h1>
          <p className="mt-6 text-lg text-secondary max-w-xl leading-relaxed">{t("hero_sub")}</p>

          <div className="mt-8 flex flex-wrap gap-3" data-testid="hero-cta-group">
            <a href={`tel:${CONTACT.phoneRaw}`} className="inline-flex items-center gap-2 bg-navy hover:bg-navy-light text-white rounded-full px-7 py-3.5 font-medium transition-all hover:-translate-y-0.5 shadow-[0_10px_30px_rgba(10,37,64,0.25)]" data-testid="hero-call-btn">
              <Phone className="w-4 h-4" /> {t("call_now")}
            </a>
            <a href={waLink()} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-7 py-3.5 font-medium transition-all hover:-translate-y-0.5 shadow-[0_10px_30px_rgba(16,185,129,0.3)]" data-testid="hero-whatsapp-btn">
              <MessageCircle className="w-4 h-4" /> {t("whatsapp")}
            </a>
            <a href={mapsUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border-2 border-navy text-navy hover:bg-navy hover:text-white rounded-full px-7 py-3.5 font-medium transition-all" data-testid="hero-directions-btn">
              <MapPin className="w-4 h-4" /> {t("get_directions")}
            </a>
          </div>

          <a href={`tel:${CONTACT.phoneRaw}`} className="mt-7 inline-flex items-center gap-3 group" data-testid="hero-phone-display">
            <span className="w-11 h-11 rounded-full bg-turq/10 flex items-center justify-center text-turq-dark"><Phone className="w-5 h-5" /></span>
            <span>
              <span className="block text-xs text-muted uppercase tracking-wider">Call us anytime</span>
              <span className="block text-xl font-bold text-navy group-hover:text-turq-dark transition-colors">{CONTACT.phoneDisplay}</span>
            </span>
          </a>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }} className="relative">
          <div className="rounded-[2rem] overflow-hidden shadow-[0_30px_80px_-20px_rgba(10,37,64,0.4)]">
            <img src={HERO_IMG} alt="Modern diagnostic laboratory equipment" className="w-full h-[400px] md:h-[520px] object-cover" />
          </div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
            className="absolute -left-4 md:-left-8 top-10 bg-white/90 backdrop-blur-xl rounded-2xl p-4 shadow-xl border border-white/60" data-testid="hero-float-card-1">
            <div className="flex items-center gap-1 text-amber-400 mb-1">{[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-current" />)}</div>
            <p className="text-2xl font-bold text-navy font-heading">5000+</p>
            <p className="text-xs text-secondary">Happy Patients</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65 }}
            className="absolute -right-3 md:-right-6 bottom-10 bg-navy rounded-2xl p-4 shadow-xl text-white" data-testid="hero-float-card-2">
            <p className="text-2xl font-bold font-heading text-turq-light">Same Day</p>
            <p className="text-xs text-white/70">Digital Reports</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
